import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type {
  ArchiveDTO,
  ArchiveGroup,
  DashboardStats,
  DetectionStatus,
  FollowReportDTO,
  MeetingDetectionDTO,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useSse } from "../hooks/useSse";
import type { SseEvent } from "../hooks/useSse";
import { Topbar } from "../components/Topbar";
import { StatsCards } from "../components/StatsCards";
import { AlertPanel } from "../components/AlertPanel";
import { MeetingTable } from "../components/MeetingTable";
import { ArchiveList } from "../components/ArchiveList";
import { FollowReport, PreviewModal } from "../components/FollowReport";
import { QuickCreateModal } from "../components/QuickCreateModal";
import { useToast } from "../components/Toast";
import { MEETING_TYPE_LABEL, periodOptions, todayKey } from "../utils/format";

export function Dashboard() {
  const { user } = useAuth();
  const { show } = useToast();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [detections, setDetections] = useState<MeetingDetectionDTO[]>([]);
  const [groups, setGroups] = useState<ArchiveGroup[]>([]);
  const [depts, setDepts] = useState<{ id: string; name: string }[]>([]);
  const [report, setReport] = useState<FollowReportDTO | null>(null);
  const [reportDeptId, setReportDeptId] = useState<string | null>(null);
  const [periods] = useState(() => periodOptions());
  const [period, setPeriod] = useState(() => periodOptions()[0].value);
  const [loadingArchives, setLoadingArchives] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState<ArchiveDTO | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  const canOverride = user?.role === "ADMIN" || user?.role === "DEPT_LEAD";
  const canQuickCreate = user?.role === "ADMIN";
  // 纪要编辑仅管理员（非管理员网页端只读，修改走企微回复提交流程）
  const canEditArchive = user?.role === "ADMIN";

  /* ================= 数据加载 ================= */

  const loadStatsAndDetections = useCallback(async () => {
    const [s, d] = await Promise.all([api.meetings.stats(), api.meetings.detections({})]);
    setStats(s);
    setDetections(d);
  }, []);

  const loadArchives = useCallback(async () => {
    setLoadingArchives(true);
    try {
      setGroups(await api.archives.grouped({}));
    } finally {
      setLoadingArchives(false);
    }
  }, []);

  const loadReport = useCallback(async (deptId: string, periodValue: string) => {
    setLoadingReport(true);
    try {
      setReport(await api.followups.get(deptId, periodValue));
    } catch {
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  }, []);

  // 初始加载：统计/检测/纪要/部门
  useEffect(() => {
    void loadStatsAndDetections();
    void loadArchives();
    void api.followups.depts().then(setDepts).catch(() => setDepts([]));
  }, [loadStatsAndDetections, loadArchives]);

  // 默认选中部门：本人部门 > 第一个部门
  useEffect(() => {
    if (reportDeptId || depts.length === 0) return;
    const preferred = user?.deptId && depts.some((d) => d.id === user.deptId) ? user.deptId : depts[0].id;
    setReportDeptId(preferred);
  }, [depts, reportDeptId, user?.deptId]);

  // 部门 / 周期变化 → 拉取对应报告
  useEffect(() => {
    if (!reportDeptId) return;
    void loadReport(reportDeptId, period);
  }, [reportDeptId, period, loadReport]);

  /* ================= SSE 实时事件 ================= */

  const handleSse = useCallback(
    (event: SseEvent) => {
      const payload = event.payload as Record<string, string> | undefined;
      switch (event.type) {
        case "detection.updated":
        case "alert.resolved":
          void loadStatsAndDetections();
          break;
        case "alert.raised":
          void loadStatsAndDetections();
          if (payload?.deptName) show(`⚠️ ${payload.deptName} 会议未按时开启`);
          break;
        case "archive.created":
          void loadArchives();
          if (payload?.name) show(`📄 新纪要已归档：${payload.name}`);
          break;
        case "report.generated":
          // 其他成员生成报告时不打扰本端，仅静默刷新当前报告
          if (reportDeptId) void loadReport(reportDeptId, period);
          break;
        default:
          break;
      }
    },
    [loadStatsAndDetections, loadArchives, loadReport, reportDeptId, period, show],
  );

  const { connected } = useSse(handleSse);

  /* ================= 派生数据 ================= */

  const archiveTotal = useMemo(() => groups.reduce((sum, g) => sum + g.count, 0), [groups]);
  // 「今日新增」按业务日期统计（归档的会议日期），而非记录入库时间
  const today = todayKey();
  const todayNewArchives = useMemo(
    () => groups.reduce((sum, g) => sum + g.items.filter((a) => a.bizDate === today).length, 0),
    [groups, today],
  );
  const deptCount = useMemo(() => new Set(detections.map((d) => d.deptId)).size, [detections]);
  const deptOptions = useMemo(
    () => (user?.role === "ADMIN" ? depts : depts.filter((d) => d.id === user?.deptId)),
    [depts, user?.role, user?.deptId],
  );

  /* ================= 交互 ================= */

  const handleBellClick = () => {
    const missed = detections.filter((d) => d.status === "MISSED");
    if (missed.length === 0) {
      show("暂无未处理通知");
      return;
    }
    show(`当前有 ${missed.length} 场会议未开启：${missed.map((m) => `${m.deptName}${MEETING_TYPE_LABEL[m.type] ?? m.type}`).join("、")}`);
  };

  const handleOverride = async (detection: MeetingDetectionDTO, status: DetectionStatus) => {
    try {
      await api.meetings.overrideDetection(detection.id, status);
      show(`已将「${detection.deptName}」的会议状态修正为${status === "DONE" ? "已开启" : status === "MISSED" ? "未开启" : status === "IN_PROGRESS" ? "即将开始" : "未到时间"}`);
      await loadStatsAndDetections();
    } catch (err) {
      show(err instanceof Error ? err.message : "修正失败");
    }
  };

  const handleGenerate = async () => {
    if (!reportDeptId) return;
    setGenerating(true);
    try {
      const fresh = await api.followups.generate(reportDeptId, period);
      setReport(fresh);
      show("跟进报告已由大模型重新生成 ✓");
    } catch (err) {
      show(err instanceof Error ? err.message : "生成失败");
    } finally {
      setGenerating(false);
    }
  };

  if (!stats) {
    return (
      <>
        <Topbar
          connected={connected}
          alertCount={0}
          onBellClick={() => {}}
          canQuickCreate={canQuickCreate}
          onQuickCreate={() => setQuickCreateOpen(true)}
        />
        <div className="container">
          <div className="loading-box loading-lg">
            <span className="spinner" />
            正在加载监测数据…
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        connected={connected}
        alertCount={stats.missed}
        onBellClick={handleBellClick}
        canQuickCreate={canQuickCreate}
        onQuickCreate={() => setQuickCreateOpen(true)}
      />

      <div className="container">
        <StatsCards
          stats={stats}
          archiveTotal={archiveTotal}
          todayNewArchives={todayNewArchives}
          deptCount={deptCount}
        />

        <AlertPanel detections={detections} />

        <div className="main-grid">
          <MeetingTable
            detections={detections}
            deptOptions={deptOptions}
            canOverride={canOverride}
            onOverride={(d, s) => void handleOverride(d, s)}
          />
          <ArchiveList
            groups={groups}
            loading={loadingArchives}
            onPreview={setPreview}
            canEdit={canEditArchive}
            onChanged={loadArchives}
          />
        </div>

        <FollowReport
          report={report}
          deptOptions={depts}
          periodOptions={periods}
          selectedDeptId={reportDeptId}
          period={period}
          loading={loadingReport}
          generating={generating}
          onDeptChange={setReportDeptId}
          onPeriodChange={setPeriod}
          onGenerate={() => void handleGenerate()}
        />

        <div className="footer-note">
          会议效能监测与议程跟进平台 · 检测引擎每分钟自动巡检 · 数据实时推送
          {user?.role === "MEMBER" ? " · 成员角色为只读视图" : ""}
        </div>
      </div>

      <PreviewModal archive={preview} onClose={() => setPreview(null)} />

      <QuickCreateModal
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onCreated={() => {
          void loadStatsAndDetections();
        }}
      />
    </>
  );
}
